import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, Input, ViewEncapsulation, HostBinding, OnChanges, SimpleChanges, ElementRef, ViewChild, inject } from '@angular/core';
import { Router, Route } from '@angular/router';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { AuthorizeGuard } from '@jms/tenant/module.jms.authorisation';
import { CommandCenterSecurityRight, FeatureFlags, TenantSubscription } from '@jms/model';
import { ApplicationActions, ApplicationState } from '@jms/tenant/module.jms.state';
import { RightsService } from '@jms/utils/util.permissions';
import * as Enumerable from 'linq';
import { SideNavItemBaseComponent } from './sidenav.item.base.component';
import { SideNavItemComponent } from './sidenav.item.component';
import { HelpNavComponent } from '../help.nav/help.nav.component';
import { UserNavComponent } from '../user.nav/user.nav.component';
import { UploaderModule } from '@jms/ui/ui.uploader';
import { InboxBadgeService } from './inbox.badge.service';

@Component({
    selector: 'side-nav',
    templateUrl: 'sidenav.component.html',
    styleUrls: ['sidenav.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [CommonModule, CdkScrollable, SideNavItemComponent, HelpNavComponent, UserNavComponent, UploaderModule]
})
export class SideNavComponent implements OnInit, OnChanges {

    protected readonly inboxBadgeService = inject(InboxBadgeService);
    private readonly rights = inject(RightsService);

    @Input() @HostBinding('class.expanded') toggle = false;
    @Input() public state: ApplicationState;

    @HostBinding('class.nav-dev')
    protected get isDev() {
        return !ConfigService?.environment?.production;
    }

    public signingEndpoint: string;
    public successEndpoint: string;
    public accountUrl: string;

    public config: Route[];
    public inboxRoute: Route;
    public agentRoute: Route;
    public settingRoute: Route;

    public buildVersion: string;
    public scrollTop = 0;
    public offsetTop = 0;

    public plan: TenantSubscription | 'Pro';

    @ViewChild('actionArea', { read: ElementRef, static: true }) actionArea: ElementRef;

    constructor(
        public router: Router,
        public actions: ApplicationActions,
        private authorizeGuard: AuthorizeGuard
    ) {

        const plan = ConfigService.environment?.plan?.subscription;
        this.plan = plan === TenantSubscription.Professional ? 'Pro' : plan;
    }

    ngOnInit() {

        this.setupConfig();
        this.signingEndpoint = '/api/attachments/sas';
        this.successEndpoint = '/api/attachments/success';

        const environment = ConfigService.environment;
        this.buildVersion = environment.build === 'undefined-undefined'
            ? 'Dev Version'
            : environment.build;

        this.accountUrl = `${environment.jms.loginHref}/account`;

        if (this.inboxRoute) this.inboxBadgeService.loadUnreadCount();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['state']) {
            this.setupConfig();
        }
    }

    public onPreventTabKey(e: any) {
        e.preventDefault();
    }

    private setupConfig() {
        const legacy = false;
        this.config = this.router.config[0].children
            .filter(x => !x.redirectTo)
            .filter(x => (legacy ? x.data.title === 'Technicians' : true))
            .filter(x => {
                return this.authorizeGuard.canLoadPath(x.path);
            })
            .filter(x => {
                if (x?.data?.hidden) return false;
                if (this.state) {
                    const restrictions = x.data.restrictions as string[];
                    return restrictions != null && restrictions.indexOf(this.state.info.tenantStatus) >= 0;
                }
                return false;
            })
            .map(x => {
                if (!x?.children?.length) {
                    return x;
                }

                const filteredChildren = x.children
                    .filter(child => {
                        return this.authorizeGuard.canLoadPath(`/${x.path}/${child.path}`);
                    })
                    .filter(child => {
                        if (child?.data?.hidden) return false;

                        if (this.state) {
                            const restrictions = child.data.restrictions as string[];
                            return restrictions != null && restrictions.indexOf(this.state.info.tenantStatus) >= 0;
                        }

                        return false;
                    });

                if (filteredChildren.length == 1) {
                    const childRoute = filteredChildren[0];
                    return {
                        ...childRoute,
                        path: `${x.path}/${childRoute.path}`,
                    }
                }

                return { ...x, children: filteredChildren };

            });

        this.inboxRoute = Enumerable
            .from(this.config)
            .where(x => x.path === 'inbox')
            .firstOrDefault();

        const canViewInbox = this.rights.hasRights(CommandCenterSecurityRight.CanViewInbox);
        const canViewQueues = this.rights.hasRights(CommandCenterSecurityRight.CanViewQueues);
        if (!canViewInbox && !canViewQueues) this.inboxRoute = null;

        const isAiAssistantEnabled = ConfigService.environment.featureFlags?.[FeatureFlags.AIAssistant] ?? false;
        this.agentRoute = isAiAssistantEnabled
            ? Enumerable.from(this.config).where(x => x.path === 'agent').firstOrDefault()
            : null;

        this.settingRoute = Enumerable
            .from(this.config)
            .where(x => x.path === 'settings')
            .firstOrDefault();

        this.config = Enumerable
            .from(this.config)
            .where(x => x.path !== 'settings' && x.path !== 'inbox' && x.path !== 'ai-assistant' && x.path !== 'agent')
            .toArray();

    }

    public onLogout() {
        if (window.Intercom) {
            window.Intercom('shutdown');
        }

        this.actions.logout(true)
    }

    public onCloseOtherMenu() {
        SideNavItemBaseComponent.attached$.next(null);
    }
}
